import React from "react";
import Seo from "../components/Seo";

const Page = ({ title, kicker, seoDescription, path, children }) => (
  <div className="max-w-3xl mx-auto px-4 md:px-8 py-14 prose-io" data-testid={`policy-${title.toLowerCase().replace(/\s+/g, "-")}`}>
    <Seo title={title} description={seoDescription} path={path}/>
    <div className="eyebrow" style={{ color: "#D4AF37" }}>{kicker}</div>
    <h1 className="font-serif text-[44px] md:text-[56px] mt-2 mb-8 text-[var(--io-text)]">{title}</h1>
    {children}
  </div>
);

export const About = () => (
  <Page
    title="About IndiaODR"
    kicker="About"
    path="/about"
    seoDescription="IndiaODR is an AI-assisted, human-reviewed legal intelligence platform covering global and Indian arbitration, ODR, mediation, conciliation, and award enforcement."
  >
    <p>IndiaODR is an AI-assisted, human-reviewed legal intelligence platform covering global and Indian arbitration, online dispute resolution, mediation, conciliation, award enforcement, and investor-state disputes. We publish original, source-backed news analysis for general counsel, arbitration practitioners, and business leaders.</p>
    <p>We do not republish or rewrite articles from other publishers. Every story we publish adds original legal context, business impact, and a clearly-attributed source trail. Our editorial process combines automated relevance scoring, AI-assisted drafting, and mandatory human review — with sensitive stories held for manual approval.</p>
    <h2>Our commitments</h2>
    <ul>
      <li>Clear attribution for every source.</li>
      <li>No unsupported factual claims.</li>
      <li>No legal advice; disclaimer on every article.</li>
      <li>Neutral legal tone; careful framing for pending matters, allegations, and sanctions.</li>
      <li>Audit logs of every AI draft and editorial decision.</li>
    </ul>
  </Page>
);

export const EditorialPolicy = () => (
  <Page
    title="Editorial Policy"
    kicker="Policy"
    path="/editorial-policy"
    seoDescription="How IndiaODR selects, drafts and publishes arbitration news — sourcing, relevance scoring, AI drafting, mandatory editorial review, and disclaimer."
  >
    <p>This page describes how IndiaODR selects, drafts and publishes arbitration news.</p>
    <h2>Sourcing</h2>
    <p>We monitor official institutions (ICC, ICSID, UNCITRAL, SIAC, LCIA, HKIAC, MCIA), court websites, and reputable legal news publications. We respect robots.txt and do not scrape paywalled content. We link to and attribute sources in every article.</p>
    <h2>Relevance scoring</h2>
    <p>Each prospective story is scored on institution importance, global impact, India relevance, ODR relevance, business/MSME relevance, search potential, freshness, and legal sensitivity. Only high-scoring stories proceed to AI drafting.</p>
    <h2>AI drafting &amp; transparency</h2>
    <p>We use large language models to draft article structure — summary, what happened, key legal issue, why it matters, timeline, business impact, India/ODR relevance, FAQ and metadata. AI-generated output is <strong>never</strong> published without human editorial review. Every article carries an <em>AI-assisted · Human reviewed · Source-backed</em> transparency trio.</p>
    <h2>Editorial review</h2>
    <p>Every draft passes through an editorial queue with AI confidence, plagiarism and legal-risk indicators. Sensitive stories — pending disputes, allegations, sanctions, investment-treaty claims, award annulment, court challenges, named individuals — require explicit manual approval.</p>
    <h2>No legal advice</h2>
    <p>We publish news analysis. Our articles are not legal advice and should not be relied upon as such.</p>
  </Page>
);

export const CorrectionPolicy = () => (
  <Page
    title="Correction Policy"
    kicker="Policy"
    path="/correction-policy"
    seoDescription="IndiaODR correction policy — how to report a factual error, our response timeline, and how corrections are flagged."
  >
    <p>Accuracy is foundational to our newsroom. If you believe an article contains a factual error, a misattribution, or inadequate context, please write to <a href="mailto:corrections@indiaodr.in">corrections@indiaodr.in</a> with the article URL and a description of the issue.</p>
    <h2>How we respond</h2>
    <ul>
      <li>Confirmed factual corrections are made promptly and noted at the foot of the article with the correction date.</li>
      <li>Material updates (new developments, retraction, or withdrawal) are flagged at the top of the article.</li>
      <li>Minor copy edits that do not affect meaning are not logged.</li>
      <li>We do not remove articles in response to reputational complaints, but we will consider contextual additions where warranted.</li>
    </ul>
  </Page>
);

export default function Policy() { return null; }
